import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mic, MicOff, Send, X } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';

interface OppOption {
  id: string;
  name: string;
}

/**
 * Story 58.4 — voice input on the rep cockpit.
 *
 * Floating mic button. Click → starts a Web Speech recognition session
 * (no audio is uploaded — transcription is on-device). The transcript
 * stays editable in a small composer; the rep picks an open opportunity
 * and saves the result as a comment, so the note ends up on the timeline
 * exactly like a typed comment.
 *
 * Browsers without SpeechRecognition (Firefox, older Safari) hide the
 * mic and let the user type instead — fallback rather than block.
 */
export function VoiceNoteDock({ openOpps }: { openOpps: OppOption[] }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [oppId, setOppId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const recogRef = useRef<SpeechRecognitionLike | null>(null);

  // Default to the first opp once the list arrives.
  useEffect(() => {
    if (!oppId && openOpps.length > 0) setOppId(openOpps[0].id);
  }, [openOpps, oppId]);

  const supported = typeof window !== 'undefined' &&
    !!(window as unknown as { SpeechRecognition?: unknown; webkitSpeechRecognition?: unknown })
      .SpeechRecognition ||
    !!(window as unknown as { webkitSpeechRecognition?: unknown }).webkitSpeechRecognition;

  function startListening() {
    const ctor = getSpeechRecognitionCtor();
    if (!ctor) {
      setStatus(t('voiceNote.unsupported'));
      return;
    }
    const recog = new ctor() as SpeechRecognitionLike;
    recog.continuous = true;
    recog.interimResults = true;
    recog.lang = navigator.language || 'en-US';
    recog.onresult = (event: SpeechRecognitionResultEventLike) => {
      let final = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const r = event.results[i];
        if (r.isFinal) final += r[0].transcript;
        else final += r[0].transcript;
      }
      setTranscript((prev) => (prev ? `${prev} ${final}`.replace(/\s+/g, ' ') : final));
    };
    recog.onerror = () => {
      setListening(false);
      setStatus(t('voiceNote.mic_error'));
    };
    recog.onend = () => setListening(false);
    recog.start();
    recogRef.current = recog;
    setListening(true);
    setStatus(null);
  }

  function stopListening() {
    recogRef.current?.stop();
    recogRef.current = null;
    setListening(false);
  }

  async function save() {
    if (!oppId || !transcript.trim()) return;
    setSubmitting(true);
    setStatus(null);
    try {
      await apiClient.post('/comments', {
        opportunityId: oppId,
        body: transcript.trim(),
      });
      setTranscript('');
      setStatus(t('voiceNote.saved'));
      setTimeout(() => {
        setStatus(null);
        setOpen(false);
      }, 1200);
    } catch {
      setStatus(t('voiceNote.save_error'));
    } finally {
      setSubmitting(false);
    }
  }

  if (!supported) return null;

  return (
    <>
      {!open && (
        <button
          type="button"
          aria-label={t('voiceNote.open_aria')}
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-30 flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition hover:scale-105"
        >
          <Mic className="h-5 w-5" />
        </button>
      )}

      {open && (
        <div className="fixed bottom-6 right-6 z-40 w-80 rounded-lg border bg-card p-4 shadow-xl">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold">{t('voiceNote.title')}</h3>
            <button
              type="button"
              onClick={() => {
                stopListening();
                setOpen(false);
              }}
              className="text-muted-foreground hover:text-foreground"
              aria-label={t('common.close')}
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="space-y-2">
            <select
              value={oppId}
              onChange={(e) => setOppId(e.target.value)}
              className="w-full rounded-md border bg-transparent px-2 py-1.5 text-xs"
            >
              <option value="">{t('voiceNote.pick_opp')}</option>
              {openOpps.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>

            <textarea
              value={transcript}
              onChange={(e) => setTranscript(e.target.value)}
              placeholder={t('voiceNote.placeholder')}
              rows={4}
              className="w-full rounded-md border bg-transparent px-2 py-1.5 text-xs"
            />

            {status && <p className="text-xs text-muted-foreground">{status}</p>}

            <div className="flex items-center justify-between gap-2">
              <Button
                size="sm"
                variant={listening ? 'destructive' : 'outline'}
                onClick={listening ? stopListening : startListening}
              >
                {listening ? (
                  <>
                    <MicOff className="mr-1 h-3.5 w-3.5" />
                    {t('voiceNote.stop')}
                  </>
                ) : (
                  <>
                    <Mic className="mr-1 h-3.5 w-3.5" />
                    {t('voiceNote.record')}
                  </>
                )}
              </Button>
              <Button
                size="sm"
                onClick={save}
                disabled={submitting || !oppId || !transcript.trim()}
              >
                <Send className="mr-1 h-3.5 w-3.5" />
                {submitting ? t('common.saving') : t('voiceNote.save')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

interface SpeechRecognitionResultLike {
  isFinal: boolean;
  [index: number]: { transcript: string };
}
interface SpeechRecognitionResultEventLike {
  resultIndex: number;
  results: { length: number; [index: number]: SpeechRecognitionResultLike };
}
interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionResultEventLike) => void) | null;
  onerror: ((event: unknown) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
}

function getSpeechRecognitionCtor(): (new () => SpeechRecognitionLike) | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}
